import {EdgeTypes, NodeTypes, Node as ReactFlowNode} from 'reactflow';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import SchemaNode from 'schemas/data-model-diagram/SchemaNode';
import {RelationshipsForSchema, SchemaDomainTypeInterface} from 'schemas/data-model-diagram/types';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

export interface SchemaDataForDiagram {
  label: string;
  highlight?: boolean;
  fadeout?: boolean;
  id: string;
  domainTypeInterface: string;
  fields: Array<{
    name: string;
    index: number;
    iconName: BenchlingIconName;
    isMulti: boolean;
    typeName: string;
  }>;
  relationships: RelationshipsForSchema;
}

export type SchemaDiagramNode = ReactFlowNode<SchemaDataForDiagram, 'schema'>;

export const SchemaDiagramEdgeTypes = {} satisfies EdgeTypes;

export const SchemaDiagramNodeTypes = {
  schema: SchemaNode,
} satisfies NodeTypes;

export const NODE_HEIGHT = 20;
export const NODE_WIDTH = 100;

export const DIAGRAM_HEIGHT = 800;
export const DIAGRAM_WIDTH = 1500;

export const INTERFACE_ICONS: Record<SchemaDomainTypeInterface, BenchlingIconName> = {
  [SchemaDomainTypeInterface.IDnaSequence]: FolderItemIcon.Sequence,
  [SchemaDomainTypeInterface.IDnaOligo]: FolderItemIcon.Oligo,
  [SchemaDomainTypeInterface.IRnaSequence]: FolderItemIcon.RNASequence,
  [SchemaDomainTypeInterface.IRnaOligo]: FolderItemIcon.OligoV2,
  [SchemaDomainTypeInterface.IAaSequence]: FolderItemIcon.Protein,
  [SchemaDomainTypeInterface.IMolecule]: FolderItemIcon.Molecule,
  [SchemaDomainTypeInterface.IPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IMatrixPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IWellPlate]: ModelTypeConstants.PLATE,
  [SchemaDomainTypeInterface.IContainer]: ModelTypeConstants.CONTAINER,
  [SchemaDomainTypeInterface.IBox]: ModelTypeConstants.BOX,
  [SchemaDomainTypeInterface.ILocation]: ModelTypeConstants.LOCATION,
  [SchemaDomainTypeInterface.IMixture]: FolderItemIcon.Mixture,
  [SchemaDomainTypeInterface.ICustomEntity]: FolderItemIcon.BasicFolderItem,
  [SchemaDomainTypeInterface.IEntry]: FolderItemIcon.Entry,
  [SchemaDomainTypeInterface.IResult]: ModelTypeConstants.RESULT_SCHEMA,
  [SchemaDomainTypeInterface.IRun]: ModelTypeConstants.RUN_SCHEMA,
};

const INVENTORY_COLOR = '#C7C9D0';

export const SCHEMA_COLORS: Record<SchemaDomainTypeInterface, string> = {
  [SchemaDomainTypeInterface.IAaSequence]: '#FDEE94',
  [SchemaDomainTypeInterface.IBox]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IContainer]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.ICustomEntity]: '#98D8E7',
  [SchemaDomainTypeInterface.IDnaOligo]: '#C0E5D8',
  [SchemaDomainTypeInterface.IDnaSequence]: '#C3FB7D',
  [SchemaDomainTypeInterface.IEntry]: '#8CB0D8',
  [SchemaDomainTypeInterface.ILocation]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IMatrixPlate]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IMixture]: '#C3B2DF',
  [SchemaDomainTypeInterface.IMolecule]: '#CD998A',
  [SchemaDomainTypeInterface.IPlate]: INVENTORY_COLOR,
  [SchemaDomainTypeInterface.IResult]: '#F0AE6E',
  [SchemaDomainTypeInterface.IRnaSequence]: '#A0AFCF',
  [SchemaDomainTypeInterface.IRnaOligo]: '#87C3AB',
  [SchemaDomainTypeInterface.IRun]: '#F1A2CB',
  [SchemaDomainTypeInterface.IWellPlate]: INVENTORY_COLOR,
};

const INVENTORY_BACKGROUND_COLOR = '#EEF1FA';

export const SCHEMA_BACKGROUND_COLORS: Record<SchemaDomainTypeInterface, string> = {
  [SchemaDomainTypeInterface.IAaSequence]: '#FFF6C3',
  [SchemaDomainTypeInterface.IBox]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IContainer]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.ICustomEntity]: '#CFE9EF',
  [SchemaDomainTypeInterface.IDnaOligo]: '#D9F1E9',
  [SchemaDomainTypeInterface.IDnaSequence]: '#E9FFCE',
  [SchemaDomainTypeInterface.IEntry]: '#C2D5EA',
  [SchemaDomainTypeInterface.ILocation]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IMatrixPlate]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IMixture]: '#DDD3ED',
  [SchemaDomainTypeInterface.IMolecule]: '#F9D0C4',
  [SchemaDomainTypeInterface.IPlate]: INVENTORY_BACKGROUND_COLOR,
  [SchemaDomainTypeInterface.IResult]: '#FFDFBF',
  [SchemaDomainTypeInterface.IRnaSequence]: '#E2E9F6',
  [SchemaDomainTypeInterface.IRnaOligo]: '#CBEBDE',
  [SchemaDomainTypeInterface.IRun]: '#F4D4E4',
  [SchemaDomainTypeInterface.IWellPlate]: INVENTORY_BACKGROUND_COLOR,
};

export const SCHEMA_DIAGRAM_COORDINATES_PREF_KEY = 'DATA_MODEL_COORDINATES';
